import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { getUserSubscription, cancelSubscription, getActivePlans, getAllSubscriptions, adminCancelSubscription } from '../api/subscription';
import { subscriptionRefreshManager } from '../utils/subscriptionRefresh';

// Hook to get active plans
export const useActivePlans = () => {
  const {
    data: plans,
    isLoading,
    error,
    refetch
  } = useQuery('activePlans', getActivePlans, {
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to fetch plans');
    },
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
  });

  return {
    plans,
    isLoading,
    error,
    refetchPlans: refetch
  };
};

export const useUserSubscription = () => {
  const { setError } = useAuthStore();

  const {
    data: subscriptionData,
    isLoading,
    error,
    refetch
  } = useQuery('userSubscription', getUserSubscription, {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch subscription details');
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    subscriptionData,
    isLoading,
    error,
    refetchSubscription: refetch
  };
};

// Hook to force refresh subscription data (useful after payment success)
export const useRefreshSubscription = () => {
  const queryClient = useQueryClient();
  
  const refreshSubscription = () => {
    // Invalidate and refetch subscription data
    queryClient.invalidateQueries('userSubscription');
    const result = queryClient.refetchQueries('userSubscription');
    
    // Also trigger global refresh for other components
    subscriptionRefreshManager.refresh();
    
    return result;
  };
  
  return { refreshSubscription };
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const cancelSubscriptionMutation = useMutation(cancelSubscription, {
    onSuccess: () => {
      toast.success('Subscription cancelled successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('userSubscription');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to cancel subscription';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    cancelSubscription: cancelSubscriptionMutation.mutate,
    isLoading: cancelSubscriptionMutation.isLoading,
    isSuccess: cancelSubscriptionMutation.isSuccess,
    isError: cancelSubscriptionMutation.isError,
    error: cancelSubscriptionMutation.error,
    reset: cancelSubscriptionMutation.reset
  };
};

// Admin: Fetch all subscriptions
export const useAdminAllSubscriptions = () => {
  const { setError } = useAuthStore();
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery('adminAllSubscriptions', getAllSubscriptions, {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch subscriptions');
    },
    retry: false,
    staleTime: 60000,
    cacheTime: 300000,
  });
  return {
    subscriptions: data?.subscriptions || [],
    isLoading,
    error,
    refetch
  };
};

// Admin: Cancel any subscription by ID
export const useAdminCancelSubscription = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();
  const mutation = useMutation(adminCancelSubscription, {
    onSuccess: () => {
      toast.success('Subscription cancelled successfully!');
      queryClient.invalidateQueries('adminAllSubscriptions');
    },
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to cancel subscription');
      toast.error(error?.response?.data?.message || 'Failed to cancel subscription');
    },
  });
  return mutation;
}; 